import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import {
  getSnowplow,
  buildInterventionEventBody,
  getInterventionsAirbrake,
  IDMEmitter,
} from "../../utils";
import { useChopinQuestion, useInactivity } from "../../hooks";
import type { Snowplow } from "@simplybusiness/services";
import type {
  InterventionsProps,
  InterventionMessage,
  InterventionEvent,
  InterventionRecords,
} from "../../types";

const getTradeFromChopinData = (props: InterventionsProps) => {
  //Consolidate trade selector answers into a single trade value
  if (props.primaryTrade !== undefined) {
    return props.primaryTrade;
  } else if (props.originalShopType !== undefined) {
    return props.originalShopType;
  } else if (props.profession !== undefined) {
    return props.profession;
  }

  return undefined;
};

const fireMissingReqAirbrake = (type: string) => {
  getInterventionsAirbrake().notify({
    error: new Error(`A ${type} is required for Interventions to work`),
    params: {
      source: "mobius-interventions",
      component: "Interventions",
      userId: null,
    },
  });
};

/**
 * Interventions
 * - creates a Snowplow instance
 * - listens for changes to target questions
 * - creates the IDMEmitter and starts polling
 * - fires publishInterventionEvent when interventions are received or data changes
 *
 * @param props Interventions props bundle
 * @returns null
 */
export const Interventions = (props: InterventionsProps) => {
  const {
    uid,
    duid,
    site,
    vertical,
    snowplowAppId,
    avalancheCollector,
    snowplowCookieDomain,
    disablePolling,
  } = props;

  const shouldNotPoll = disablePolling || !duid || !site;

  const snowplowInstance: Snowplow = useMemo(() => {
    return getSnowplow({
      uid: uid,
      duid: duid || null,
      appId: snowplowAppId,
      avalancheCollector: avalancheCollector,
      cookieDomain: snowplowCookieDomain,
      site: site,
    });
  }, [
    uid,
    duid,
    snowplowAppId,
    avalancheCollector,
    snowplowCookieDomain,
    site,
  ]);
  const snowplowRef = useRef<Snowplow>(snowplowInstance);

  const idmEmitter = useRef<IDMEmitter | null>(null);
  const [interventions, setInterventions] = useState<InterventionRecords>({}); // Interventions and their components we have been told to show
  const [popupShowing, setPopupShowing] = useState<string | null>(null);
  const isInactive = useInactivity();
  const { answer: trade } = useChopinQuestion("trade_selector");
  const { answer: turnover } = useChopinQuestion("annual_turnover");
  const { answer: yearsTrading } = useChopinQuestion("years_trading");
  const { answer: postcode } = useChopinQuestion("uk_postcode");
  const { answer: businessType } = useChopinQuestion("business_type");
  const { answer: town } = useChopinQuestion("town");

  // Create an object using the current chopin question answers with prop data as fallback
  const chopinPageData = useMemo(() => {
    return {
      popupShowing: popupShowing,
      inactive: isInactive,
      trade: trade !== undefined ? trade : getTradeFromChopinData(props),
      businessType:
        businessType !== undefined ? businessType : props.businessType,
      annualTurnover: turnover !== undefined ? turnover : props.annualTurnover,
      yearsTrading:
        yearsTrading !== undefined ? yearsTrading : props.yearsTrading,
      ukPostcode: postcode !== undefined ? postcode : props.ukPostcode,
      town: town !== undefined ? town : props.town,
    };
  }, [
    props,
    trade,
    turnover,
    yearsTrading,
    postcode,
    town,
    businessType,
    popupShowing,
    isInactive,
  ]);

  const publishInterventionEvent = useCallback(
    (
      interventionName: string,
      componentName: string,
      category: string,
      message: InterventionMessage,
      duid: string,
      site: string,
    ) => {
      const event = {
        interventionName: interventionName,
        componentName: componentName,
        message: message,
      } as InterventionEvent;
      if (category === "popup" && !componentName.includes("control")) {
        event.onPopupShow = () => setPopupShowing(interventionName);
        event.onPopupClose = () => setPopupShowing(null);
      }
      event.fireEvent = (action: string) => {
        void snowplowRef.current.trackUnstructEvent(
          buildInterventionEventBody(interventionName, duid, site, action),
        );
      };
      window.dispatchEvent(
        new CustomEvent("intervention-event", {
          detail: event,
        }),
      );
    },
    [],
  );

  // Create an IDMEmitter if we have the required information and start polling for interventions
  useEffect(() => {
    if (!duid) fireMissingReqAirbrake("DUID");
    if (!site) fireMissingReqAirbrake("Site");
    if (disablePolling)
      console.warn(
        "[WARNING] Interventions polling is disabled, use DevTools to enable.",
      );
    if (shouldNotPoll) return;
    if (idmEmitter.current) return;

    // callback to update interventions state when new interventions are received from IDM polling
    const interventionReceivedCallback = (
      interventionName: string,
      componentNames: string[],
      category: string,
      contextData?: Record<string, unknown>,
    ) => {
      setInterventions((prev: InterventionRecords) => {
        return {
          ...prev,
          [interventionName]: {
            componentNames: componentNames,
            category: category,
            contextData: contextData,
          },
        };
      });
    };

    idmEmitter.current = new IDMEmitter(
      duid,
      site,
      vertical as string | undefined,
      interventionReceivedCallback,
    );
    idmEmitter.current.startWithoutImmediatePoll(); // the idmEmitter useEffect below will set the data and poll immediately after this
  }, [disablePolling, shouldNotPoll, duid, site, vertical]);

  //setEligibilityData is isolated in its own useEffect so that it will only occur when chopinPageData have been changed
  useEffect(() => {
    if (idmEmitter.current) {
      idmEmitter.current.setEligibilityData(chopinPageData);
      idmEmitter.current.pollNow();
    }
  }, [chopinPageData]);

  //Whenever any value changes or a new intervention is received, fire events for all interventions we have been asked to show, with most recent data
  useEffect(() => {
    if (shouldNotPoll) return;

    Object.entries(interventions).forEach(([interventionName, components]) => {
      components.componentNames.forEach(componentName => {
        const interventionMessage: InterventionMessage = {
          show: true, //@TODO: currently hardcoded. AHOY-3303 contains some detail about possible implementations to undo this.
          contextData: components.contextData,
        };

        publishInterventionEvent(
          interventionName,
          componentName,
          components.category,
          interventionMessage,
          duid,
          site,
        );
      });
    });
  }, [
    shouldNotPoll,
    interventions,
    chopinPageData,
    duid,
    site,
    publishInterventionEvent,
  ]);

  return null;
};
